import './style.scss';
import React, { useState, useMemo, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ButtonCertificate, Text } from '../../atoms';
import { PlaceholderTable } from 'pctComponents/placehoader';
import { useFetch } from 'pctHooks/useFetch';
import { MdUnfoldMore } from 'react-icons/md';
import { AiOutlineEye } from 'react-icons/ai';
import { convertToBrDate, getProfileInfo } from '../../../services/utils';
import { getCertificate } from '../../../services/api';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import ModalLoadingInfo from '../../modals/ModalLoadingInfo';
import AlertComponent from '../AlertComponent';
import ImpactEvaluationIcon from './components/ImpactEvaluationIcon';
import { EnrollmentStatus } from 'types/evaluation';

interface Props {
    rawHistory: any[];
    impactEvaluations: any[];
    historyError: string | null;
    loading?: boolean;
}

const columns = [
    { header: 'Título', accessor: 'title' },
    { header: 'Status', accessor: 'enrollmentStatus' },
    { header: 'Conclusão', accessor: 'finishDate' },
    { header: 'Expira em', accessor: 'validityDate' },
];

const statusTranslate = (currentStatusTranslate: string) => {
    const arrStatus = ['NOT_ATTEMPTED', 'IN_PROGRESS', 'COMPLETED', 'PASSED', 'FAILED', 'EXPIRED', 'EQUIVALENCY'];
    const arrStatusTranslate = [
        'Não iniciado',
        'Em progresso',
        'Concluído',
        'Aprovado',
        'Reprovado',
        'Expirado',
        'Concluído por equivalência',
    ];
    const translateWordIndex = arrStatus.indexOf(currentStatusTranslate);
    return translateWordIndex >= 0 ? arrStatusTranslate[translateWordIndex] : '-';
};

const filterAcessor = (column: (typeof columns)[number], dataRow: any) => {
    switch (column.accessor) {
        case 'finishDate':
        case 'validityDate':
            return convertToBrDate(dataRow[column.accessor]);
        case 'grade':
            return dataRow[column.accessor] ?? '-';
        case 'enrollmentStatus':
            return statusTranslate(dataRow[column.accessor]);
        default:
            return dataRow[column.accessor];
    }
};

const MyHistoric: React.FC<Props> = ({ rawHistory, impactEvaluations, historyError, loading }) => {
    NiceModal.register('ModalLoadingInfo', ModalLoadingInfo);
    const modalLoading = useModal('ModalLoadingInfo');
    const history = useHistory();
    const [sortType, setSortType] = useState('');

    const getImpactEvaluationForEnrollment = useCallback(
        (idEnrollment: number) => {
            return impactEvaluations.find((evaluation) => evaluation.idEnrollment === idEnrollment);
        },
        [impactEvaluations],
    );

    const shouldShowImpactEvaluation = useCallback(
        (item: any) => {
            const hasCompletedStatus =
                item.enrollmentStatus === EnrollmentStatus.COMPLETED ||
                item.enrollmentStatus === EnrollmentStatus.PASSED;
            const hasValidEnrollment = !!item.idEnrollment;
            const hasImpactEvaluation = !!getImpactEvaluationForEnrollment(item.idEnrollment);
            return hasCompletedStatus && hasValidEnrollment && hasImpactEvaluation;
        },
        [getImpactEvaluationForEnrollment],
    );

    const handleImpactEvaluationClick = useCallback(
        (item: any) => {
            const impactEvaluation = getImpactEvaluationForEnrollment(item.idEnrollment);
            if (impactEvaluation) {
                history.push(
                    `/evaluations/${impactEvaluation.idEnrollment}/${impactEvaluation.idEvaluation}/${impactEvaluation.idQuestionnaire}`,
                );
            }
        },
        [getImpactEvaluationForEnrollment, history],
    );

    const getImpactEvaluationStatus = useCallback(
        (item: any) => {
            const impactEvaluation = getImpactEvaluationForEnrollment(item.idEnrollment);
            if (!impactEvaluation) {
                return { isDisabled: true, title: 'Avaliação não disponível', className: 'disabled' };
            }
            if (impactEvaluation.finishDate) {
                return { isDisabled: true, title: 'Avaliação de impacto já respondida', className: 'disabled' };
            }
            if (impactEvaluation.expirationDate && new Date(impactEvaluation.expirationDate) < new Date()) {
                return { isDisabled: true, title: 'Avaliação de impacto expirada', className: 'disabled' };
            }
            return { isDisabled: false, title: 'Acessar avaliação de impacto', className: '' };
        },
        [getImpactEvaluationForEnrollment],
    );

    const currentData = useMemo(() => {
        if (!rawHistory) return [];
        if (sortType) {
            return [...rawHistory].sort((a, b) => (a[sortType] > b[sortType] ? 1 : -1));
        }
        return rawHistory;
    }, [rawHistory, sortType]);

    const handleCertificateMouseEvent = useCallback(
        (item: any, e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            modalLoading.show({
                obj: {
                    message: 'Aguarde, gerando e baixando o certificado!',
                    closeBtn: false,
                    loadding: true,
                },
            });
            getCertificate({
                idUser: getProfileInfo().idUser,
                idEnrollment: item.idEnrollment,
            })
                .then((response) => {
                    setTimeout(() => modalLoading.remove(), 2000);
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${item.title?.replace(/[^\\w]/g, '')}_certificate.pdf`;
                    link.click();
                })
                .catch(console.log);
        },
        [modalLoading],
    );

    if (historyError) return <p>There is an error.</p>;
    if (loading || modalLoading.visible) {
        return (
            <div className="myHistoricLoading">
                <PlaceholderTable rowsNumber={10} />
            </div>
        );
    }

    return (
        <div className="ptcHistory">
            {rawHistory && currentData.length === 0 ? (
                <AlertComponent message="Sem histórico." fullpage={false} />
            ) : (
                <div className="responsiveTableWrapper">
                    <table className="responsiveTable">
                        <thead>
                            <tr>
                                {columns.map((colum, i) => (
                                    <th key={i}>
                                        <button type="button" onClick={() => setSortType(colum.accessor)}>
                                            <Text component={'p'} text={colum.header} />
                                            <MdUnfoldMore />
                                        </button>
                                    </th>
                                ))}
                                <th>
                                    <Text className="alignLeft" component={'p'} text={'Ações'} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.map((item, j) => {
                                const hasImpactEvaluation = shouldShowImpactEvaluation(item);
                                const hasLinkAccess =
                                    item.nodeStatus === 'PUBLISHED' && item.enrollmentStatus !== 'EXPIRED';
                                const hasContentLink = !item.idEnrollment;
                                const hasCertificate =
                                    item.idCertificate &&
                                    (item.enrollmentStatus === 'COMPLETED' || item.enrollmentStatus === 'PASSED');
                                const showEyeIcon =
                                    item.nodeStatus === 'PUBLISHED' &&
                                    item.enrollmentStatus !== 'EXPIRED' &&
                                    item.idEnrollment &&
                                    !(
                                        (item.enrollmentStatus === 'COMPLETED' || item.enrollmentStatus === 'PASSED') &&
                                        item.allowAccessUponCompletion === 0 &&
                                        !hasImpactEvaluation
                                    );
                                const showContentEyeIcon = !item.idEnrollment;
                                const hasAnyAction =
                                    showEyeIcon || showContentEyeIcon || hasCertificate || hasImpactEvaluation;
                                return (
                                    <tr key={j}>
                                        {columns.map((col, k) => (
                                            <td key={k} data-label={col.header}>
                                                <Text component={'p'} text={filterAcessor(col, item)} />
                                            </td>
                                        ))}
                                        <td data-label="Ações">
                                            <div className="actions">
                                                {showEyeIcon && (
                                                    <Link to={`/level/${item['idEnrollment']}`}>
                                                        <AiOutlineEye />
                                                    </Link>
                                                )}
                                                {showContentEyeIcon && (
                                                    <Link to={`/contentNode/${item['idContentItem']}`}>
                                                        <AiOutlineEye />
                                                    </Link>
                                                )}
                                                {hasCertificate && (
                                                    <ButtonCertificate
                                                        onClick={(e) => handleCertificateMouseEvent(item, e)}
                                                        verifyCompletedStatus={true}
                                                    />
                                                )}
                                                {hasImpactEvaluation && (
                                                    <ImpactEvaluationIcon
                                                        status={getImpactEvaluationStatus(item)}
                                                        onClick={() => handleImpactEvaluationClick(item)}
                                                    />
                                                )}
                                                {!hasAnyAction && <>-</>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default MyHistoric;
